
import React, { useState, useEffect, useRef } from 'react';
import { twoFactorService } from '../../services/twoFactorService';
import { authService } from '../../services/authService';
import Swal from 'sweetalert2';
import Layout from '../../components/Layout';
import styles from './SecureAuth.module.css';

const SecureAuth = () => {
    const [password, setPassword] = useState('');
    const [connectionId, setConnectionId] = useState('');
    const [loading, setLoading] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [showDisableOtp, setShowDisableOtp] = useState(false);
    const [disableOtp, setDisableOtp] = useState(['', '', '', '', '', '']);
    const [selectedMethod, setSelectedMethod] = useState(null); // 'telegram' or 'mobile'
    const [mobileQrCode, setMobileQrCode] = useState(null);
    const [mobileSecret, setMobileSecret] = useState(null);
    const [mobileOtp, setMobileOtp] = useState(['', '', '', '', '', '']);
    const [isPasswordVerified, setIsPasswordVerified] = useState(false);
    const mobileInputs = useRef([]);
    const inputs = useRef([]);

    const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || '@cfzbot_bot';
    // Remove '@' if present for URL
    const botUrl = `https://t.me/${botUsername.replace('@', '')}`;

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        try {
            const response = await authService.getProfile();
            console.log('DEBUG: Profile Response', response);
            
            // Handle different response structures:
            // Owner: response.data.telegram2FAEnabled (directly in data)
            // Staff: response.data.user.telegram2FAEnabled or response.data.telegram2FAEnabled
            // Fallback: response.user.telegram2FAEnabled or response.telegram2FAEnabled
            const telegram2FAEnabled = 
                response.data?.telegram2FAEnabled ?? 
                response.data?.user?.telegram2FAEnabled ?? 
                response.user?.telegram2FAEnabled ?? 
                response.telegram2FAEnabled ?? 
                false;
            
            console.log('DEBUG: 2FA Enabled?', telegram2FAEnabled);
            setIsEnabled(!!telegram2FAEnabled);
        } catch (error) {
            console.error("Failed to fetch profile", error);
        }
    };

    const handleVerifyPassword = async () => {
        if (!password) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please enter your login password' });
            return;
        }

        setLoading(true);
        try {
            await twoFactorService.verifyPassword(password);
            setIsPasswordVerified(true);
            Swal.fire({ icon: 'success', title: 'Success', text: 'Password verified successfully', timer: 1500, showConfirmButton: false });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.error || 'Invalid password',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleGetConnectionId = async () => {
        if (!isPasswordVerified) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please verify your password first' });
            return;
        }

        setLoading(true);
        try {
            const response = await twoFactorService.generateLinkCode();
            if (response.success) {
                setConnectionId(response.code);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.error || 'Failed to generate connection ID',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEnableMobileApp = () => {
        setSelectedMethod('mobile');
        setConnectionId('');
        setIsPasswordVerified(false);
        setMobileQrCode(null);
        setMobileSecret(null);
        setMobileOtp(['', '', '', '', '', '']);
        setPassword('');
    };

    const handleEnableTelegram = () => {
        setSelectedMethod('telegram');
        setConnectionId('');
        setIsPasswordVerified(false);
        setMobileQrCode(null);
        setMobileSecret(null);
        setMobileOtp(['', '', '', '', '', '']);
        setPassword('');
    };

    const handleGenerateMobileSetup = async () => {
        if (!isPasswordVerified) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please verify your password first' });
            return;
        }

        setLoading(true);
        try {
            const response = await twoFactorService.generateMobileSetup();
            if (response.success) {
                setMobileQrCode(response.qrCode || response.qr);
                setMobileSecret(response.secret || response.secretKey);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.error || 'Failed to generate mobile setup',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleMobileOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newCode = [...mobileOtp];
        newCode[index] = value;
        setMobileOtp(newCode);
        if (value && index < 5) mobileInputs.current[index + 1].focus();
        if (newCode.every(d => d !== '') && index === 5 && value) {
            verifyMobileSetup(newCode.join(''));
        }
    };

    const handleMobileKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !mobileOtp[index] && index > 0) {
            mobileInputs.current[index - 1].focus();
        }
    };

    const verifyMobileSetup = async (otpValue) => {
        setLoading(true);
        try {
            await twoFactorService.verifyMobileSetup(otpValue);
            setIsEnabled(true);
            setSelectedMethod(null);
            setMobileQrCode(null);
            setMobileSecret(null);
            setMobileOtp(['', '', '', '', '', '']);
            setPassword('');
            setIsPasswordVerified(false);
            Swal.fire({ icon: 'success', title: 'Success', text: 'Mobile 2FA has been enabled successfully' });
            fetchStatus();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.error || 'Invalid OTP' });
            setMobileOtp(['', '', '', '', '', '']);
            if (mobileInputs.current[0]) mobileInputs.current[0].focus();
        } finally {
            setLoading(false);
        }
    };

    const initiateDisable = async () => {
        setLoading(true);
        try {
            await twoFactorService.sendDisableCode();
            setShowDisableOtp(true);
            Swal.fire({ icon: 'success', title: 'OTP Sent', text: 'Please check your Telegram for the code', timer: 1500, showConfirmButton: false });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.error || 'Failed to send OTP' });
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newCode = [...disableOtp];
        newCode[index] = value;
        setDisableOtp(newCode);
        if (value && index < 5) inputs.current[index + 1].focus();
        if (newCode.every(d => d !== '') && index === 5 && value) {
            submitDisable(newCode.join(''));
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !disableOtp[index] && index > 0) {
            inputs.current[index - 1].focus();
        }
    };

    const submitDisable = async (otpValue) => {
        setLoading(true);
        try {
            await twoFactorService.disable2FA(otpValue);
            setIsEnabled(false);
            setShowDisableOtp(false);
            setDisableOtp(['', '', '', '', '', '']);
            Swal.fire({ icon: 'success', title: 'Success', text: '2FA has been disabled successfully' });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.error || 'Invalid OTP' });
            setDisableOtp(['', '', '', '', '', '']); // Clear on error
            // Focus first input
            if (inputs.current[0]) inputs.current[0].focus();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout title="Secure Verify - Diamond Admin">
            <div style={{
                background: 'rgba(0, 0, 0, 0.4)',
                padding: '10px  18px',
            }} className="h-full text-white  pb-4">
                <div className="border-b border-[1px solid #cae0e8] pb-2 mb-6">
                    <div className="text-[24px] font-normal text-[#cae0e8]">Secure Auth Verification</div>
                </div>

                <div className="max-w-2xl mx-auto text-center">
                    <div className="mb-2 flex items-center justify-center gap-1">
                        <div className="text-white mt-2 font-bold">Secure Auth Verification Status: </div>
                        {isEnabled ? (
                            <div
                            style={{
                                padding: '1px 8px',
                                borderRadius: '4px',
                            }}
                                onClick={!showDisableOtp ? initiateDisable : undefined}
                                disabled={loading}
                                className={`px-2 py-0.5 text-[10px] mt-2 font-bold bg-green-600 hover:bg-green-700 transition-colors ${loading ? '' : ''}`}
                            >
                                Enabled
                            </div>
                        ) : (
                            <div style={{
                                background: '#dc3545',
                                padding: '1px 8px',
                                borderRadius: '4px',
                            }} className="px-2 py-0.5 text-[10px] font-bold mt-2 text-white">Disabled</div>
                        )}
                    </div>

                    {!isEnabled ? (
                        <>
                            <p className="text-white mb-2">Please select below option to enable secure auth verification</p>

                            <div className="flex justify-center mb-6">
                                <button 
                                style={{fontSize: '14px'}}
                                    onClick={handleEnableMobileApp}
                                    className={`px-6 py-2 border-r border-gray-500 transition-colors ${
                                        selectedMethod === 'mobile' 
                                            ? 'bg-[#333d79] text-white hover:bg-[#404c96]' 
                                            : 'bg-[#666] text-white hover:bg-gray-700'
                                    }`}
                                >
                                    Enable Using Mobile App
                                </button>
                                <button 
                                style={{fontSize: '14px'}}
                                    onClick={handleEnableTelegram}
                                    className={`px-6 py-2 transition-colors ${
                                        selectedMethod === 'telegram' 
                                            ? 'bg-[#333d79] text-white hover:bg-[#404c96]' 
                                            : 'bg-[#666] text-white hover:bg-gray-700'
                                    }`}
                                >
                                    Enable Using Telegram
                                </button>
                            </div>

                            {selectedMethod === 'telegram' && (
                                <div className=" p-8">
                                    <p className="text-white font-bold mb-2">Please enter your login password to continue</p>

                                    {!isPasswordVerified ? (
                                        <div className="flex justify-center items-center gap-2 mb-6">
                                            <input
                                                type="password"
                                                className="px-3 py-2 bg-white text-black  h-[32px] focus:outline-none"
                                                // placeholder="Enter password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleVerifyPassword()}
                                            />
                                            <button
                                                className="px-4 py-2 bg-[#333d79] hover:bg-[#404c96] text-white text-sm font-medium transition-colors"
                                                onClick={handleVerifyPassword}
                                                disabled={loading}
                                            >
                                                Get Connection ID
                                                {/* {loading ? 'Verifying...' : 'Verify Password'} */}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex justify-center items-center gap-2 mb-6">
                                            <button
                                                className="px-4 py-2 bg-[#333d79] hover:bg-[#404c96] text-white text-sm font-medium transition-colors"
                                                onClick={handleGetConnectionId}
                                                disabled={loading}
                                            >
                                                Get Connection ID
                                                {/* {loading ? 'Generating...' : 'Get Connection ID'} */}
                                            </button>
                                        </div>
                                    )}

                                    {connectionId && (
                                        <div className="bg-[#1f5058] p-2 mt-4 text-center ">
                                            <p className="text-white font-bold mb-1">Please follow below instructions for the telegram 2-step verification</p>
                                            <p className="text-white font-bold mb-1">
                                                Find <a style={{color: '#007bff'}} href={botUrl} target="_blank" rel="noopener noreferrer" className="text-[#007bff] font-semibold hover:underline">{botUsername}</a> in your telegram and type <span className="text-white font-mono px-1 rounded">/start</span> Command. Bot will respond you.
                                            </p>
                                            <p className="text-white font-bold mb-4">
                                                After this type <span className="text-white font-mono px-1 rounded">/connect {connectionId}</span> and send it to BOT
                                            </p>
                                            <p className="text-white font-bold">
                                                Now your telegram account will be linked with your website account and 2-step verification will be enabled
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedMethod === 'mobile' && (
                                <div className="bg-[#1e2342] p-8 rounded shadow-lg border border-[#2a2f55]">
                                    <p className="text-white mb-4">Please enter your login password to continue</p>

                                    {!isPasswordVerified ? (
                                        <div className="flex justify-center items-center gap-2 mb-6">
                                            <input
                                                type="password"
                                                className="px-3 py-2 bg-white text-black rounded w-64 focus:outline-none"
                                                placeholder="Enter password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleVerifyPassword()}
                                            />
                                            <button
                                                className="px-4 py-2 bg-[#333d79] hover:bg-[#404c96] text-white rounded text-sm font-medium transition-colors"
                                                onClick={handleVerifyPassword}
                                                disabled={loading}
                                            >
                                                {loading ? 'Verifying...' : 'Verify Password'}
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            {!mobileQrCode ? (
                                                <div className="flex justify-center mb-6">
                                                    <button
                                                        className="px-4 py-2 bg-[#333d79] hover:bg-[#404c96] text-white rounded text-sm font-medium transition-colors"
                                                        onClick={handleGenerateMobileSetup}
                                                        disabled={loading}
                                                    >
                                                        {loading ? 'Generating...' : 'Generate QR Code'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="space-y-6">
                                                    <div className="bg-[#2a4a4a] border border-[#3e6666] p-4 rounded">
                                                        <p className="text-white mb-4 text-center">Scan this QR code with your authenticator app</p>
                                                        <div className="flex justify-center mb-4">
                                                            {mobileQrCode && (
                                                                <img 
                                                                    src={mobileQrCode} 
                                                                    alt="QR Code" 
                                                                    className="bg-white p-2 rounded"
                                                                    style={{ maxWidth: '250px', height: 'auto' }}
                                                                />
                                                            )}
                                                        </div>
                                                        {mobileSecret && (
                                                            <div className="mt-4">
                                                                <p className="text-gray-300 text-sm mb-2 text-center">Or enter this secret key manually:</p>
                                                                <div className="bg-black p-2 rounded text-center">
                                                                    <code className="text-white font-mono text-sm break-all">{mobileSecret}</code>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="bg-[#2a4a4a] border border-[#3e6666] p-4 rounded">
                                                        <p className="text-white mb-4 text-center">Enter the 6-digit code from your authenticator app to verify</p>
                                                        <div className="flex justify-center gap-4 mb-4">
                                                            {mobileOtp.map((digit, index) => (
                                                                <input
                                                                    key={index}
                                                                    ref={el => mobileInputs.current[index] = el}
                                                                    type="text"
                                                                    maxLength={1}
                                                                    value={digit}
                                                                    onChange={(e) => handleMobileOtpChange(index, e.target.value)}
                                                                    onKeyDown={(e) => handleMobileKeyDown(index, e)}
                                                                    className="w-12 h-12 bg-white rounded text-center text-xl text-gray-800 font-bold focus:outline-none focus:ring-2 focus:ring-green-500"
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* {!selectedMethod && (
                                <div className="bg-[#1e2342] p-8 rounded shadow-lg border border-[#2a2f55]">
                                    <p className="text-gray-400 text-center">Please select an option above to enable secure auth verification</p>
                                </div>
                            )} */}
                        </>
                    ) : (
                        // Disable Flow UI
                        <div className="mt-8">
                            {showDisableOtp ? (
                                <div className="">
                                    <h3 className="text-xl text-white mb-2">Security Code Verification using Telegram</h3>
                                    <p className="text-white mb-6">Enter 6-digit code received in <a style={{color: '#007bff'}} href={botUrl} target="_blank" rel="noopener noreferrer" className="text-[#007bff] hover:underline">{botUsername}</a> Bot.</p>

                                    <div className="flex justify-center mb-4">
                                        <div className={styles.otpContainer} data-is-input-num="true">
                                            {disableOtp.map((digit, index) => (
                                                <div key={index} className={styles.otpInputWrapper}>
                                                    <input
                                                        ref={el => inputs.current[index] = el}
                                                        type="tel"
                                                        min="0"
                                                        max="9"
                                                        maxLength={1}
                                                        pattern="[0-9]"
                                                        value={digit}
                                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                                        className={styles.otpInput}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-gray-400 mt-4">
                                    {/* <p>2FA is currently enabled. Click the green "Enabled" button above to disable it.</p> */}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default SecureAuth;
